/**
 * Malware Scanner Section
 * 
 * Features:
 * - Malware scanning (quick, standard, full)
 * - Scan result visualization with real-time progress
 * - Scan history with expandable details
 * - Module-based findings display
 */


import { useState, useEffect, useRef } from 'react';
import {
  Card,
  Typography,
  Tag,
  Button,
  Space,
  Empty,
  Spin,
  Row,
  Col,
  Divider,
  Table,
  Collapse,
  Badge,
  Popconfirm,
  List,
  Steps,
  App,
} from 'antd';
import {
  SecurityScanOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
  FileSearchOutlined,
  DatabaseOutlined,
  FolderOutlined,
  KeyOutlined,
  DeleteOutlined,
  SafetyOutlined,
  BugOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useThemeStore } from '@/stores/theme';
import { queryKeys } from '@/lib/queryKeys';

const { Title, Text } = Typography;

/** Labels for scan modules — must match WP plugin SCAN_TIERS module names */
const MODULE_LABELS: Record<string, string> = {
  initializing: 'Initializing',
  core_integrity: 'Core Integrity Check',
  malware_signatures: 'Malware Signature Scan',
  suspicious_files: 'Suspicious File Detection',
  htaccess: '.htaccess Analysis',
  database: 'Database Anomalies',
  entropy_analysis: 'Entropy Analysis',
  permissions: 'File Permissions',
};

interface MalwareSectionProps {
  project: any;
}

export default function MalwareSection({ project }: MalwareSectionProps) {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const hasLsmConnection = !!project.has_health_check_secret;

  // Progress polling state
  const [scanProgress, setScanProgress] = useState<any>(null);
  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch latest security scan
  const { data: latestScan, isLoading: isLoadingScan, refetch: refetchScan } = useQuery({
    queryKey: queryKeys.projects.securityScanLatest(project.id),
    queryFn: () => api.lsm.getLatestScan(project.id).then(r => r.data?.data || r.data),
    enabled: hasLsmConnection,
    staleTime: 60000,
  });

  // Fetch scan history
  const { data: scanHistory, isLoading: isLoadingHistory } = useQuery({
    queryKey: queryKeys.projects.securityScans(project.id),
    queryFn: () => api.lsm.getSecurityScans(project.id, 10).then(r => r.data?.data || r.data),
    enabled: hasLsmConnection,
    staleTime: 60000,
  });

  // Trigger scan mutation
  const scanMutation = useMutation({
    mutationFn: ({ scanType }: { scanType: 'full' | 'standard' | 'quick' }) =>
      api.lsm.triggerSecurityScan(project.id, scanType),
    onSuccess: () => {
      message.success('Security scan completed!');
      setScanProgress(null);
      refetchScan();
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(project.id) });
    },
    onError: (error: any) => {
      setScanProgress(null);
      message.error(error?.response?.data?.message || 'Scan failed. Ensure the site is reachable.');
    },
  });

  // Delete scan mutation
  const deleteScanMutation = useMutation({
    mutationFn: (scanId: number) => api.lsm.deleteSecurityScan(project.id, scanId),
    onSuccess: () => {
      message.success('Scan deleted');
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(project.id) });
    },
    onError: () => {
      message.error('Failed to delete scan');
    },
  });

  // Poll scan progress while scanning
  useEffect(() => {
    if (scanMutation.isPending) {
      // Start polling after a short delay to let the transient be created
      const timeout = setTimeout(() => {
        progressInterval.current = setInterval(async () => {
          try {
            const res = await api.lsm.getScanProgress(project.id);
            const data = res.data;
            if (data?.scanning && data?.data) {
              setScanProgress(data.data);
            }
          } catch {
            // Silently ignore polling errors
          }
        }, 2000);
      }, 1000);

      return () => {
        clearTimeout(timeout);
        if (progressInterval.current) {
          clearInterval(progressInterval.current);
          progressInterval.current = null;
        }
      };
    } else {
      // Cleanup when not scanning
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    }
  }, [scanMutation.isPending, project.id]);

  // Show empty state if not connected
  if (!hasLsmConnection) {
    return (
      <Empty
        image={<SecurityScanOutlined style={{ fontSize: 48, color: '#94a3b8' }} />}
        description={<Text type="secondary">Connect WordPress to enable malware scanning</Text>}
      />
    );
  }

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'critical': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#f59e0b';
      case 'low': return '#22c55e';
      case 'clean': return '#22c55e';
      default: return '#94a3b8';
    }
  };

  const getRiskTag = (risk: string) => {
    const colorMap: Record<string, string> = {
      critical: 'red',
      high: 'orange',
      medium: 'gold',
      low: 'green',
      clean: 'green',
    };
    const labelMap: Record<string, string> = {
      critical: '🔴 Critical',
      high: '🟠 High Risk',
      medium: '🟡 Medium',
      low: '🟢 Low Risk',
      clean: '✅ Clean',
    };
    return <Tag color={colorMap[risk] || 'default'}>{labelMap[risk] || risk}</Tag>;
  };

  const getModuleIcon = (module: string) => {
    switch (module) {
      case 'core_integrity': return <SafetyOutlined />;
      case 'malware_signatures': return <BugOutlined />;
      case 'suspicious_files': return <FileSearchOutlined />;
      case 'htaccess': return <FolderOutlined />;
      case 'database': return <DatabaseOutlined />;
      case 'entropy_analysis': return <SecurityScanOutlined />;
      case 'permissions': return <KeyOutlined />;
      default: return <FolderOutlined />;
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>Malware Scanner</Title>
      </div>

      {/* Main Scanner Card */}
      <Card
        title={
          <Space>
            <SecurityScanOutlined style={{ color: '#8b5cf6' }} />
            <span>Scanner</span>
            {latestScan?.risk_level && getRiskTag(latestScan.risk_level)}
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ThunderboltOutlined />}
              size="small"
              onClick={() => scanMutation.mutate({ scanType: 'quick' })}
              loading={scanMutation.isPending && scanMutation.variables?.scanType === 'quick'}
              disabled={scanMutation.isPending}
            >
              Quick Scan
            </Button>
            <Button
              icon={<SecurityScanOutlined />}
              size="small"
              onClick={() => scanMutation.mutate({ scanType: 'standard' })}
              loading={scanMutation.isPending && scanMutation.variables?.scanType === 'standard'}
              disabled={scanMutation.isPending}
              style={{ background: '#3b82f6', borderColor: '#3b82f6', color: '#fff' }}
            >
              Standard Scan
            </Button>
            <Button
              type="primary"
              icon={<SecurityScanOutlined />}
              size="small"
              onClick={() => scanMutation.mutate({ scanType: 'full' })}
              loading={scanMutation.isPending && scanMutation.variables?.scanType === 'full'}
              disabled={scanMutation.isPending}
              style={{ background: '#8b5cf6', borderColor: '#8b5cf6' }}
            >
              Full Scan
            </Button>
          </Space>
        }
        style={{
          borderRadius: 12,
          background: isDark ? '#1e293b' : '#fff',
          marginBottom: 16,
        }}
      >
        {scanMutation.isPending ? (
          <div style={{ padding: '24px 16px' }}>
            {scanProgress?.modules ? (() => {
              const modules = scanProgress.modules as Record<string, { status: string; findings?: number; completed_at?: string }>;
              const moduleKeys = Object.keys(modules).filter(k => k !== 'initializing');

              // Calculate progress percentage
              const completedCount = moduleKeys.filter(k => modules[k].status === 'completed').length;
              const totalCount = moduleKeys.length;
              const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Spin size="small" />
                      <Text strong>
                        Scanning {scanMutation.variables?.scanType === 'quick' ? '(Quick)' : scanMutation.variables?.scanType === 'standard' ? '(Standard)' : '(Full)'}
                      </Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {completedCount}/{totalCount} modules · {progressPct}%
                    </Text>
                  </div>

                  {/* Progress bar */}
                  <div style={{
                    height: 4,
                    borderRadius: 2,
                    background: isDark ? '#334155' : '#e2e8f0',
                    marginBottom: 20,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      borderRadius: 2,
                      width: `${progressPct}%`,
                      background: 'linear-gradient(90deg, #8b5cf6, #6366f1)',
                      transition: 'width 0.5s ease',
                    }} />
                  </div>

                  {/* Step list */}
                  <Steps
                    direction="vertical"
                    size="small"
                    current={-1}
                    items={moduleKeys.map((key) => {
                      const mod = modules[key];
                      const isCompleted = mod.status === 'completed';
                      const isRunning = mod.status === 'running';

                      let icon;
                      if (isCompleted) {
                        icon = <CheckCircleOutlined style={{ color: '#22c55e' }} />;
                      } else if (isRunning) {
                        icon = <LoadingOutlined style={{ color: '#8b5cf6' }} />;
                      } else {
                        icon = <ClockCircleOutlined style={{ color: '#94a3b8' }} />;
                      }

                      const findings = mod.findings ?? 0;
                      const description = isCompleted
                        ? findings > 0
                          ? `${findings} finding${findings > 1 ? 's' : ''}`
                          : 'Clean'
                        : isRunning
                          ? 'In progress...'
                          : 'Waiting';

                      return {
                        title: (
                          <Text style={{ fontSize: 13, color: isRunning ? (isDark ? '#fff' : '#0f172a') : undefined }}>
                            {MODULE_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Text>
                        ),
                        description: (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {description}
                          </Text>
                        ),
                        icon,
                        status: (isCompleted ? 'finish' : isRunning ? 'process' : 'wait') as any,
                      };
                    })}
                  />
                </>
              );
            })() : (
              /* Fallback spinner before progress data arrives */
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">Starting scan...</Text>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {scanMutation.variables?.scanType === 'quick'
                      ? 'This should take about 30 seconds'
                      : scanMutation.variables?.scanType === 'standard'
                        ? 'This may take 1-2 minutes'
                        : 'This may take up to 5 minutes'}
                  </Text>
                </div>
              </div>
            )}
          </div>
        ) : isLoadingScan ? (
          <Spin />
        ) : latestScan ? (
          <div>
            {/* Latest scan summary */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}>
                <div style={{
                  textAlign: 'center',
                  padding: 12,
                  borderRadius: 8,
                  background: isDark ? '#0f172a' : '#f8fafc',
                }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: getRiskColor(latestScan.risk_level) }}>
                    {latestScan.threats_found}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Threats</Text>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  textAlign: 'center',
                  padding: 12,
                  borderRadius: 8,
                  background: isDark ? '#0f172a' : '#f8fafc',
                }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>
                    {latestScan.warnings_found}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Warnings</Text>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  textAlign: 'center',
                  padding: 12,
                  borderRadius: 8,
                  background: isDark ? '#0f172a' : '#f8fafc',
                }}>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {latestScan.files_scanned}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Files Scanned</Text>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{
                  textAlign: 'center',
                  padding: 12,
                  borderRadius: 8,
                  background: isDark ? '#0f172a' : '#f8fafc',
                }}>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {latestScan.duration_seconds != null ? `${latestScan.duration_seconds}s` : 'N/A'}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Duration</Text>
                </div>
              </Col>
            </Row>

            {/* Last scan time */}
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Last scanned: {latestScan.completed_at ? new Date(latestScan.completed_at).toLocaleString() : 'N/A'}
                {' · '}
                Type: <Tag style={{ fontSize: 11 }}>{latestScan.scan_type}</Tag>
              </Text>
            </div>

            {/* Scan findings details */}
            {(() => {
              const moduleData = latestScan.results?.results || latestScan.results?.findings;
              if (!moduleData || typeof moduleData !== 'object' || Object.keys(moduleData).length === 0) return null;
              return (
                <Collapse
                  size="small"
                  style={{ marginBottom: 12 }}
                  items={Object.entries(moduleData).map(([module, data]: [string, any]) => {
                    // Extract items based on module type
                    let items: any[] = [];
                    if (module === 'core_integrity') {
                      const unknown = (data.unknown_files || []).map((f: any) => ({ ...f, reason: 'Unknown file in WordPress core' }));
                      const modified = (data.modified_files || []).map((f: any) => ({ ...f, reason: 'Modified core file' }));
                      const missing = (data.missing_files || []).map((f: any) => ({ ...f, reason: 'Missing core file', severity: 'medium' }));
                      items = [...unknown, ...modified, ...missing];
                    } else {
                      items = data.findings || data.issues || [];
                    }
                    return {
                      key: module,
                      label: (
                        <Space>
                          {getModuleIcon(module)}
                          <span>{module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                          {items.length > 0 ? (
                            <Badge count={items.length} style={{ backgroundColor: items.some((i: any) => i.severity === 'critical' || i.severity === 'high') ? '#ef4444' : '#f59e0b' }} />
                          ) : (
                            <Tag color="green" style={{ fontSize: 11 }}>{data.status === 'clean' ? '✓ Clean' : 'No issues'}</Tag>
                          )}
                        </Space>
                      ),
                      children: (
                        <List
                          size="small"
                          dataSource={items}
                          locale={{ emptyText: 'No issues found' }}
                          renderItem={(issue: any) => (
                            <List.Item>
                              <List.Item.Meta
                                title={
                                  <Space>
                                    <Tag color={issue.severity === 'critical' ? 'red' : issue.severity === 'high' ? 'orange' : 'gold'}>
                                      {issue.severity}
                                    </Tag>
                                    <Text style={{ fontSize: 13 }}>{issue.reason || issue.description || issue.message}</Text>
                                  </Space>
                                }
                                description={
                                  <Space direction="vertical" size={0}>
                                    {issue.file && (
                                      <Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>{issue.file}</Text>
                                    )}
                                    {issue.current_permissions && (
                                      <Text type="secondary" style={{ fontSize: 11 }}>
                                        Permissions: {issue.current_permissions} → recommended: {issue.recommended}
                                      </Text>
                                    )}
                                  </Space>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    };
                  })}
                />
              );
            })()}

            {/* Scan History */}
            {scanHistory && scanHistory.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <HistoryOutlined style={{ color: '#94a3b8' }} />
                  <Text strong style={{ fontSize: 13 }}>Scan History</Text>
                </div>
                <Table
                  size="small"
                  dataSource={scanHistory}
                  rowKey="id"
                  pagination={false}
                  expandable={{
                    expandedRowRender: (record: any) => {
                      const moduleData = record.results?.results || record.results?.findings || record.results?.modules;
                      if (!moduleData || typeof moduleData !== 'object' || Object.keys(moduleData).length === 0) {
                        return <Text type="secondary" style={{ fontSize: 12 }}>No detailed findings available for this scan.</Text>;
                      }

                      const moduleEntries = Object.entries(moduleData).map(([moduleName, data]: [string, any]) => {
                        let items: any[] = [];
                        if (moduleName === 'core_integrity') {
                          const unknown = (data.unknown_files || []).map((f: any) => ({ ...f, reason: 'Unknown file in WordPress core', category: 'Unknown' }));
                          const modified = (data.modified_files || []).map((f: any) => ({ ...f, reason: 'Modified core file', category: 'Modified' }));
                          const missing = (data.missing_files || []).map((f: any) => ({ ...f, reason: 'Missing core file', severity: 'medium', category: 'Missing' }));
                          items = [...unknown, ...modified, ...missing];
                        } else {
                          items = data.findings || data.issues || [];
                        }
                        return { moduleName, data, items, status: data.status };
                      });

                      return (
                        <div style={{ padding: '4px 0' }}>
                          {moduleEntries.map(({ moduleName, items, status }) => {
                            const label = moduleName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                            return (
                              <div key={moduleName} style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                  {getModuleIcon(moduleName)}
                                  <Text strong style={{ fontSize: 12 }}>{label}</Text>
                                  {items.length > 0 ? (
                                    <Badge
                                      count={items.length}
                                      style={{ backgroundColor: items.some((i: any) => i.severity === 'critical' || i.severity === 'high') ? '#ef4444' : '#f59e0b' }}
                                    />
                                  ) : (
                                    <Tag color="green" style={{ fontSize: 10 }}>{status === 'clean' ? '✓ Clean' : 'No issues'}</Tag>
                                  )}
                                </div>
                                {items.length > 0 && (
                                  <div style={{ marginLeft: 24 }}>
                                    {items.map((item: any, idx: number) => (
                                      <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '3px 0', flexWrap: 'wrap' }}>
                                        <Tag
                                          color={item.severity === 'critical' ? 'red' : item.severity === 'high' ? 'orange' : 'gold'}
                                          style={{ fontSize: 10, flexShrink: 0 }}
                                        >
                                          {item.severity}
                                        </Tag>
                                        {item.file && (
                                          <Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>{item.file}</Text>
                                        )}
                                        {item.reason && (
                                          <Text type="secondary" style={{ fontSize: 11 }}>{item.reason}</Text>
                                        )}
                                        {item.current_permissions && (
                                          <Text type="secondary" style={{ fontSize: 11 }}>
                                            (current: {item.current_permissions}, recommended: {item.recommended})
                                          </Text>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    },
                    rowExpandable: (record: any) => (
                      record.threats_found > 0 || record.warnings_found > 0 ||
                      !!record.results?.results || !!record.results?.findings
                    ),
                  }}
                  columns={[
                    {
                      title: 'Date',
                      dataIndex: 'completed_at',
                      render: (v: string) => v ? new Date(v).toLocaleDateString() : '—',
                      width: 100,
                    },
                    {
                      title: 'Type',
                      dataIndex: 'scan_type',
                      render: (v: string) => <Tag>{v}</Tag>,
                      width: 80,
                    },
                    {
                      title: 'Risk',
                      dataIndex: 'risk_level',
                      render: (v: string) => getRiskTag(v),
                      width: 120,
                    },
                    {
                      title: 'Threats',
                      dataIndex: 'threats_found',
                      width: 70,
                      render: (v: number) => <Text style={{ color: v > 0 ? '#ef4444' : undefined }}>{v}</Text>,
                    },
                    {
                      title: 'Warnings',
                      dataIndex: 'warnings_found',
                      width: 80,
                      render: (v: number) => <Text style={{ color: v > 0 ? '#f59e0b' : undefined }}>{v}</Text>,
                    },
                    {
                      title: 'Files',
                      dataIndex: 'files_scanned',
                      width: 60,
                    },
                    {
                      title: 'By',
                      dataIndex: 'triggered_by',
                      render: (v: string) => <Tag style={{ fontSize: 11 }}>{v}</Tag>,
                      width: 70,
                    },
                    {
                      title: '',
                      key: 'actions',
                      width: 40,
                      render: (_: any, record: any) => (
                        <Popconfirm
                          title="Delete this scan?"
                          onConfirm={() => deleteScanMutation.mutate(record.id)}
                          okText="Delete"
                          cancelText="Cancel"
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deleteScanMutation.isPending}
                          />
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              </div>
            )}
          </div>
        ) : (
          <Empty
            image={<SecurityScanOutlined style={{ fontSize: 40, color: '#94a3b8' }} />}
            description={
              <Space direction="vertical" size={4}>
                <Text type="secondary">No scans yet</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>Run your first scan to check for malware and vulnerabilities</Text>
              </Space>
            }
          />
        )}
      </Card>
    </div>
  );
}
